import { useState } from 'react';
import { App, Button, Drawer, Empty, InputNumber, Progress, Spin, Statistic, Tag } from 'antd';
import { PlayCircleOutlined, StopOutlined } from '@ant-design/icons';
import { container } from '@infrastructure/di/container';
import {
  useRunCollection,
  type RunnerResult,
  type RunnerRequestStatus,
} from '@presentation/hooks/useRunCollection';
import { MethodBadge } from '@presentation/components/workbench/MethodBadge';
import type { HttpMethod } from '@core/entities/ApiRequest';
import type { ApiRequest } from '@core/entities/ApiRequest';

interface CollectionRunnerDrawerProps {
  collectionId: string;
  collectionName: string;
  open: boolean;
  onClose: () => void;
}

const STATUS_COLOR: Record<RunnerRequestStatus, string> = {
  pending: 'default',
  running: 'processing',
  passed: 'success',
  failed: 'error',
  error: 'error',
  skipped: 'default',
};

const ResultRow = ({ result }: { result: RunnerResult }) => (
  <div className="flex items-center gap-2 border-b border-black/5 py-1.5 text-xs last:border-0 dark:border-white/10">
    {result.status === 'running' ? (
      <Spin size="small" className="w-14 shrink-0" />
    ) : (
      <Tag color={STATUS_COLOR[result.status]} className="w-14 shrink-0 text-center">
        {result.status}
      </Tag>
    )}
    <MethodBadge method={result.method as HttpMethod} className="w-10 shrink-0" />
    <span className="min-w-0 flex-1 truncate">{result.requestName}</span>
    {result.testsTotal > 0 && (
      <span className="shrink-0 text-gray-400">
        {result.testsPassed}/{result.testsTotal} tests
      </span>
    )}
    {result.statusCode !== null && (
      <span className="w-10 shrink-0 text-right">{result.statusCode}</span>
    )}
    {result.durationMs !== null && (
      <span className="w-16 shrink-0 text-right text-gray-400">{result.durationMs} ms</span>
    )}
    {result.errorMessage && (
      <span className="shrink-0 text-red-500" title={result.errorMessage}>
        ⚠
      </span>
    )}
  </div>
);

export const CollectionRunnerDrawer = ({
  collectionId,
  collectionName,
  open,
  onClose,
}: CollectionRunnerDrawerProps) => {
  const { message } = App.useApp();
  const [iterations, setIterations] = useState(1);
  const [delayMs, setDelayMs] = useState(0);
  const { run, stop, isRunning, results, summary } = useRunCollection();
  const [requestCount, setRequestCount] = useState(0);

  const handleRun = async () => {
    let requests: ApiRequest[];
    try {
      requests = await container.apiRequests.listAll(collectionId);
    } catch (error) {
      void message.error(error instanceof Error ? error.message : 'Failed to load requests');
      return;
    }

    if (requests.length === 0) {
      void message.warning('This collection has no requests to run');
      return;
    }

    setRequestCount(requests.length);
    await run(requests, { iterations, delayMs });
  };

  const totalPlanned = requestCount * iterations;
  const progressPercent = totalPlanned > 0 ? Math.round((results.length / totalPlanned) * 100) : 0;

  return (
    <Drawer title={`Run "${collectionName}"`} open={open} onClose={onClose} size={640}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Iterations</span>
            <InputNumber
              size="small"
              min={1}
              max={50}
              value={iterations}
              onChange={(value) => setIterations(value ?? 1)}
              disabled={isRunning}
              className="w-16"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Delay</span>
            <InputNumber
              size="small"
              min={0}
              max={30000}
              value={delayMs}
              onChange={(value) => setDelayMs(value ?? 0)}
              disabled={isRunning}
              className="w-20"
            />
            <span className="text-xs text-gray-500">ms</span>
          </div>

          {isRunning ? (
            <Button size="small" danger icon={<StopOutlined />} onClick={stop} className="ml-auto">
              Stop
            </Button>
          ) : (
            <Button
              size="small"
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => void handleRun()}
              className="ml-auto"
            >
              Run
            </Button>
          )}
        </div>

        {(isRunning || results.length > 0) && (
          <Progress
            percent={progressPercent}
            status={isRunning ? 'active' : undefined}
            size="small"
          />
        )}

        {results.length > 0 && (
          <div className="grid grid-cols-4 gap-2 rounded border border-black/10 p-2 dark:border-white/10">
            <Statistic
              title="Passed"
              value={summary.passed}
              styles={{ content: { color: '#52c41a', fontSize: 18 } }}
            />
            <Statistic
              title="Failed"
              value={summary.failed}
              styles={{ content: { color: '#faad14', fontSize: 18 } }}
            />
            <Statistic
              title="Errored"
              value={summary.errored}
              styles={{ content: { color: '#ff4d4f', fontSize: 18 } }}
            />
            <Statistic
              title="Assertions"
              value={`${summary.assertionsPassed}/${summary.assertionsTotal}`}
              styles={{ content: { fontSize: 18 } }}
            />
          </div>
        )}

        <div className="max-h-96 overflow-y-auto">
          {results.length === 0 && !isRunning && (
            <Empty
              description="Set your options and click Run"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
          {results.map((result) => (
            <ResultRow key={result.key} result={result} />
          ))}
        </div>
      </div>
    </Drawer>
  );
};
